import React from "react";
import WebProfitSpinner from "../../components/system/WebProfitSpinner";
import { Alert, Box, Button, Link, Stack, TextField } from "@mui/material";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  AuthPageShell,
  AuthPasswordField,
  authLinkSx,
  authPrimaryButtonSx,
} from "../../components/dashboard/auth/AuthFormShell";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = String(searchParams.get("ref") || "").trim().toUpperCase();
  const loginHref = referralCode ? `/login?ref=${encodeURIComponent(referralCode)}` : "/login";
  const [form, setForm] = React.useState({ name: "", email: "", password: "" });
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const handleChange = (event) => {
    setForm((previous) => ({ ...previous, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const name = String(form.name || "").trim();
    const email = String(form.email || "").trim().toLowerCase();

    if (!name) {
      setError("Ingresá tu nombre.");
      return;
    }
    if (!email || !email.includes("@")) {
      setError("Ingresá un email válido.");
      return;
    }
    if (form.password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    try {
      setLoading(true);
      setError("");
      const data = await register({
        name,
        email,
        password: form.password,
        referralCode: referralCode || undefined,
      });

      if (data?.onboarding?.firstUser) {
        navigate("/dashboard", { replace: true });
        return;
      }

      if (data?.onboarding?.requiresEmailVerification) {
        navigate(`/confirmar-email?email=${encodeURIComponent(email)}`, {
          replace: true,
          state: {
            email,
            message: data?.onboarding?.message || "Cuenta creada. Revisá tu email para confirmarla.",
          },
        });
        return;
      }

      navigate("/login", {
        replace: true,
        state: {
          message: data?.onboarding?.message || "Cuenta creada. Ya podés ingresar.",
          messageSeverity: "success",
        },
      });
    } catch (err) {
      setError(err.message || "No se pudo crear la cuenta.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthPageShell
      title="Crear cuenta"
      subtitle="Completá tus datos para empezar."
      helpLabel="¿Qué pasa después?"
      help={
        <>
          Si tu cuenta requiere validación, te enviaremos un código por email. Al confirmarlo vas a continuar con el inicio de WebProfit. Si llegaste con un referido, el código se conserva durante este registro.
        </>
      }
      footer={
        <>
          ¿Ya tenés cuenta?{" "}
          <Link component={RouterLink} to={loginHref} sx={authLinkSx}>
            Ingresar
          </Link>
        </>
      }
    >
      {referralCode ? (
        <Alert severity="info" sx={{ py: 0.5 }}>
          Referido: <strong>{referralCode}</strong>
        </Alert>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}

      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={1.5}>
          <TextField
            label="Nombre"
            name="name"
            value={form.name}
            onChange={handleChange}
            required
            fullWidth
            disabled={loading}
            autoFocus
            autoComplete="name"
            inputProps={{ autoCapitalize: "words" }}
          />
          <TextField
            label="Email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            fullWidth
            disabled={loading}
            autoComplete="email"
            inputProps={{ inputMode: "email", autoCapitalize: "none", spellCheck: "false" }}
          />
          <AuthPasswordField
            label="Contraseña"
            name="password"
            value={form.password}
            onChange={handleChange}
            autoComplete="new-password"
            disabled={loading}
            helperText="Mínimo 8 caracteres."
          />
          <Button type="submit" disabled={loading} variant="contained" size="large" startIcon={loading ? <WebProfitSpinner size={17} /> : undefined} sx={authPrimaryButtonSx}>
            {loading ? "Creando..." : "Crear cuenta"}
          </Button>
        </Stack>
      </Box>
    </AuthPageShell>
  );
}
